/**
 * Style constants and small helpers shared between Report Studio and the
 * schedule modal it opens.
 *
 * ReportStudio.tsx imports the modal, to render it. If the modal then
 * imported these from ReportStudio.tsx, the two files would import each
 * other — which the bundler resolves by picking an evaluation order, and
 * whichever file loads second sees the other's `const` exports before they
 * are assigned. That is not a hypothetical: it is exactly how this broke —
 * `FREQUENCIES` was `undefined` in the modal, and `FREQUENCIES.map()` threw
 * before either component ever reached the screen. A third file neither
 * depends on removes the cycle rather than papering over one direction.
 */
import axios from 'axios'
import type { ScheduleFrequency } from './types'

export const FREQUENCIES: ScheduleFrequency[] = ['daily', 'weekly', 'biweekly', 'monthly', 'quarterly']
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// [color-scheme:dark] flips the native select arrow, option popup, and date-picker
// glyphs to dark rendering; optionClass covers browsers that honor per-option colors.
export const compactField =
  'mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none [color-scheme:dark] focus:border-amber-300/40'
export const optionClass = 'bg-black text-white'
export const labelClass = 'text-[9px] uppercase tracking-[0.14em] text-stone-600'
export const ghostBtn =
  'inline-flex items-center gap-1 rounded border border-stone-600 px-2 py-1 text-[10px] text-stone-300 hover:bg-white/5 disabled:opacity-50'
export const goldBtn =
  'inline-flex items-center gap-1.5 rounded bg-amber-300 px-3 py-1.5 text-[11px] font-medium text-black disabled:opacity-60'

export function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail
    if (typeof detail === 'string' && detail.trim()) return detail
    if (typeof err.message === 'string' && err.message.trim()) return err.message
  }
  if (err instanceof Error && err.message.trim()) return err.message
  return fallback
}

export function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
}
